<script setup lang="ts">
/* global __VERSIONS__ */

import smileSuiteLogo from '@/src/assets/smilesuite_logo.svg';
import { useKeyboardShortcutsStore } from '@/src/store/keyboard-shortcuts';

const keyboardStore = useKeyboardShortcutsStore();

const links = [
  {
    label: 'Website',
    description: 'Learn about SmileSuite',
    href: 'https://smilesuite.ai/',
    icon: 'mdi-web',
  },
  {
    label: 'Services',
    description: 'Custom imaging platforms',
    href: 'https://smilesuite.ai/',
    icon: 'mdi-briefcase-outline',
  },
  {
    label: 'LinkedIn',
    description: 'Follow our updates',
    href: 'https://linkedin.com/company/smilesuite/',
    icon: 'mdi-linkedin',
  },
  {
    label: 'GitHub',
    description: 'Open-source projects',
    href: 'https://github.com/pycadd',
    icon: 'mdi-github',
  },
] as const;

function openKeyboardShortcuts() {
  keyboardStore.settingsOpen = true;
}

const viewerVersion = __VERSIONS__.volview;
</script>

<template>
  <div class="pycad-about">
    <div class="pycad-about__left">
      <header class="pycad-about__hero">
        <img :src="smileSuiteLogo" alt="SmileSuite" class="pycad-about__logo" />
        <p class="pycad-about__tagline">
          AI-powered dental imaging — panoramic CBCT review, implant planning,
          and clinical workflows built for modern practices.
        </p>
      </header>

      <div class="pycad-about__actions">
        <a
          class="pycad-about__cta"
          href="https://smilesuite.ai/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i class="mdi mdi-calendar-check"></i>
          Book a call
        </a>
        <button
          type="button"
          class="pycad-about__secondary"
          @click="openKeyboardShortcuts"
        >
          <i class="mdi mdi-keyboard-outline"></i>
          Keyboard shortcuts
        </button>
      </div>

      <section class="pycad-about__section">
        <h3 class="pycad-about__heading">What we build</h3>
        <ul class="pycad-about__features">
          <li>Multi-planar DICOM viewing, 3D rendering &amp; annotations</li>
          <li>AI-powered segmentation and measurement tools</li>
          <li>Custom CRM and PACS-integrated clinical workflows</li>
          <li>Secure, HIPAA-ready platforms tailored to your domain</li>
        </ul>
      </section>
    </div>

    <div class="pycad-about__right">
      <section class="pycad-about__section">
        <h3 class="pycad-about__heading">Connect with us</h3>
        <div class="pycad-about__links">
          <a
            v-for="link in links"
            :key="link.href"
            class="pycad-about__link"
            :href="link.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="pycad-about__link-icon">
              <i class="mdi" :class="link.icon"></i>
            </span>
            <span class="pycad-about__link-text">
              <span class="pycad-about__link-label">{{ link.label }}</span>
              <span class="pycad-about__link-desc">{{ link.description }}</span>
            </span>
            <i class="mdi mdi-open-in-new pycad-about__link-arrow"></i>
          </a>
        </div>
      </section>

      <section class="pycad-about__section pycad-about__contact">
        <h3 class="pycad-about__heading">Contact</h3>
        <a class="pycad-about__email" href="mailto:contact@smilesuite.ai">
          <i class="mdi mdi-email-outline"></i>
          contact@smilesuite.ai
        </a>
      </section>

      <footer class="pycad-about__footer">
        <span>Viewer {{ viewerVersion }}</span>
        <span class="pycad-about__footer-sep">·</span>
        <span>© SmileSuite</span>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.pycad-about {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  width: 100%;
  padding: 24px 48px 20px 28px;
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 12px);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--pycad-text-primary, #fff);
  background: var(--pycad-elevated, #161616);
  box-sizing: border-box;
}

.pycad-about__left,
.pycad-about__right {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.pycad-about__right {
  justify-content: space-between;
}

.pycad-about__hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}

.pycad-about__logo {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.pycad-about__tagline {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-about__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pycad-about__cta,
.pycad-about__secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: var(--pycad-radius-sm, 6px);
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.13s ease;
  white-space: nowrap;
}

.pycad-about__cta {
  color: #111;
  background: var(--pycad-primary, #ff9900);
  border: 1px solid var(--pycad-primary, #ff9900);
}

.pycad-about__cta:hover {
  background: #ffe066;
  border-color: #ffe066;
}

.pycad-about__cta .mdi {
  font-size: 15px;
}

.pycad-about__secondary {
  color: var(--pycad-text-primary, #fff);
  background: transparent;
  border: 1px solid var(--pycad-border, #282828);
}

.pycad-about__secondary:hover {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  color: var(--pycad-primary, #ff9900);
}

.pycad-about__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pycad-about__heading {
  margin: 0;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--pycad-text-secondary, #a0a0a0);
}

.pycad-about__features {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  line-height: 1.55;
  color: var(--pycad-text-secondary, #b5b5b5);
}

.pycad-about__features li + li {
  margin-top: 2px;
}

.pycad-about__links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.pycad-about__link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 6px);
  background: var(--pycad-elevated, #161616);
  color: inherit;
  text-decoration: none;
  transition:
    border-color 0.13s ease,
    background 0.13s ease;
}

.pycad-about__link:hover {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.06));
}

.pycad-about__link-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--pycad-surface, #111);
  color: var(--pycad-primary, #ff9900);
  font-size: 16px;
  flex-shrink: 0;
}

.pycad-about__link-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.pycad-about__link-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--pycad-text-primary, #fff);
}

.pycad-about__link-desc {
  font-size: 10px;
  color: var(--pycad-text-muted, #666);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pycad-about__link-arrow {
  font-size: 12px;
  color: var(--pycad-text-muted, #666);
  flex-shrink: 0;
}

.pycad-about__contact {
  margin-top: auto;
}

.pycad-about__email {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius-sm, 6px);
  background: var(--pycad-elevated, #161616);
  font-size: 12.5px;
  font-family: 'JetBrains Mono', monospace;
  color: var(--pycad-primary, #ff9900);
  text-decoration: none;
  transition: border-color 0.13s ease;
  width: fit-content;
}

.pycad-about__email:hover {
  border-color: var(--pycad-primary-border, rgba(255, 153, 0, 0.45));
}

.pycad-about__email .mdi {
  font-size: 16px;
}

.pycad-about__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  color: var(--pycad-text-muted, #555);
}

.pycad-about__footer-sep {
  opacity: 0.5;
}

@media (max-width: 680px) {
  .pycad-about {
    grid-template-columns: 1fr;
    max-width: 420px;
  }

  .pycad-about__links {
    grid-template-columns: 1fr;
  }
}
</style>
